import React from "react";
import ReactDOM from "react-dom";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEdit, faEllipsisV, faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPaperPlane } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import EventEmitter from "events";
import { noop } from "lodash";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { openRecapModal } from "~/meetings/MeetingModal/openRecapModal";
import MiniPlayer from "~/miniPlayer";
import { Button, Tooltip } from "~/ui";
import { ButtonLink, IconButton, Modal, ToggleGroup } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import { __globalModalContext, type ModalProps, useModals } from "~/ui/modals";

import { NoteDate } from "../stream/NoteHeader/NoteHeader";
import { RenameClipModal } from "./ClipsPanel";
import { openShareClipModal } from "./ShareClipModal";
import { RecapEngagementSection } from "./StatsPanel";
import { SummaryText } from "./SummaryText";
import { LoadingFallback, transcriptModalViewerFragment } from "./TranscriptModal";
import { TranscriptModalContext, TranscriptModalContextProvider } from "./TranscriptModalContext";
import { type SpeakerTranscriptSegment, Transcript, type TranscriptRef } from "./TranscriptPanel";
import TranscriptVideo, { CollapseButton } from "./TranscriptVideo";
import { copyToClipboard, formatDuration, useGetURL, useIsNarrowViewport, useWatchStatsFeatureFlagOn } from "./utils";
import { useSessionManager } from "./Video/SessionManager";

import type { ClipModal_clip$key } from "~/__generated__/ClipModal_clip.graphql";
import type { ClipModalQuery } from "~/__generated__/ClipModalQuery.graphql";
import type { TranscriptModal_viewer$key } from "~/__generated__/TranscriptModal_viewer.graphql";

import styles from "./css/ClipModal.module.css";
import transcriptModalStyles from "./css/TranscriptModal.module.css";

const CLIP_MODAL_ID = "clip-modal";
const CLIP_MODAL_CLOSE_EVENT = "ClipModal__close";
const clipModalEmitter = new EventEmitter();

type MobileTabType = "Transcript" | "Summary" | "Stats";
type TabType = "Transcript" | "Stats";
export type ClipTabType = TabType | MobileTabType;

const clipFragment = graphql`
	fragment ClipModal_clip on MeetingRecordingClip {
		id
		clipEnd
		clipStart
		summary
		title
		transcript
		viewerCanEdit
		mediaType

		postedInChannels {
			id
		}

		baseRecording {
			id
			title
			meetingCallStart
			meetingCallEnd
			note {
				id
				... on Note {
					url
					eventStart
					eventEnd
				}

				...TranscriptPanel_note
			}
		}

		ownerAccount {
			name
			logo
		}

		...TranscriptVideo_meetingRecording @arguments(public: false)
		...TranscriptPanel_Transcript_meetingRecording @arguments(public: false)
		...utils_useGetURL_recording
		...ClipsPanel_RenameClipModal_clip
	}
`;

type ClipModalProps = ModalProps<unknown> & {
	clipKey: ClipModal_clip$key;
	viewerKey: TranscriptModal_viewer$key;
	initialVideoTimestamp?: number;
	clipTab?: ClipTabType;
};

function ClipModal({ close, clipKey, viewerKey, initialVideoTimestamp, clipTab }: ClipModalProps) {
	const clip = useFragment<ClipModal_clip$key>(clipFragment, clipKey);
	const viewer = useFragment<TranscriptModal_viewer$key>(transcriptModalViewerFragment, viewerKey);

	const isWatchStatsEnabled = useWatchStatsFeatureFlagOn();
	const isStatsEnabled = isWatchStatsEnabled && viewer.account.allowRecapEngagementStats && !viewer.user.isGuest;

	// Currently the only stats in a Clip's stats panel are the recap engagement stats
	// Since this feature can be toggled either by feature flag or account setting we need to hide the stats tab if it's not enabled
	// so that the user doesn't see a tab with no content
	// If you're adding more stats, then we can remove this check if we're sure that the stats panel will always have content
	const MOBILE_TAB_VALUES = isStatsEnabled
		? (["Transcript", "Summary", "Stats"] as const)
		: (["Transcript", "Summary"] as const);
	const TAB_VALUES = isStatsEnabled ? (["Transcript", "Stats"] as const) : (["Transcript"] as const);

	// Analytics
	const { analyticsFingerprint, setNoteId } = React.useContext(TranscriptModalContext);
	const note = clip.baseRecording?.note ?? null;
	const noteId = note?.id ?? null;

	React.useEffect(() => setNoteId(noteId), [noteId, setNoteId]);

	// Refs
	const videoRef = React.useRef<HTMLVideoElement>(null);
	const transcriptRef = React.useRef<TranscriptRef>(null);

	// Emitter to close the modal from the outside
	React.useEffect(() => {
		clipModalEmitter.on(CLIP_MODAL_CLOSE_EVENT, close);
		return () => {
			clipModalEmitter.off(CLIP_MODAL_CLOSE_EVENT, close);
		};
	}, [close]);

	// Mini player state and callbacks
	const [isMiniPlayerActive, setIsMiniPlayerActive] = React.useState<boolean>(false);
	const toggleMiniPlayer = React.useCallback(() => setIsMiniPlayerActive(prev => !prev), []);

	React.useEffect(() => {
		if (isMiniPlayerActive) {
			dispatchAnalyticsTrack("[AI Copilot] Mini Player Opened", {
				fingerprint: analyticsFingerprint,
				source: "Modal",
			});
		}
	}, [analyticsFingerprint, isMiniPlayerActive]);

	const onDimmerClickEnterMiniPlayer = React.useCallback(() => {
		// If the video is currently playing, enter the mini player. Otherwise
		// close the modal.
		if (!videoRef.current || videoRef.current.paused) {
			close();
		} else {
			toggleMiniPlayer();
		}
	}, [toggleMiniPlayer, close, videoRef]);

	// Mobile
	const narrowViewport = useIsNarrowViewport();

	// Tabs
	const [activeTab, setActiveTab] = React.useState<ClipTabType>(() => {
		// Since the tabs differ between mobile and desktop, we need to make sure the tab actually exists before using it.
		const availableTabs = narrowViewport ? MOBILE_TAB_VALUES : TAB_VALUES;
		const canUseTabProp = clipTab && availableTabs.some(tab => tab === clipTab);
		return canUseTabProp ? clipTab : availableTabs[0];
	});

	// Other callbacks
	const onVideoTimeUpdate = React.useCallback(() => {
		if (!videoRef.current) return;

		transcriptRef.current?.handleTimeUpdate(videoRef.current.currentTime);
	}, []);

	const onTranscriptSegmentClick = React.useCallback(
		(transcriptSegment: SpeakerTranscriptSegment) => {
			const video = videoRef.current;
			if (!video) return;

			// Subtract half a second to make sure you get the first word; the browser
			// might round this number.
			video.currentTime = Math.max(0, transcriptSegment.words[0].start_timestamp - 0.5);
			video.play();

			dispatchAnalyticsTrack("Clip transcript segment click", {
				fingerprint: analyticsFingerprint,
				clip_id: clip.id, // eslint-disable-line camelcase
			});
		},
		[clip, videoRef, analyticsFingerprint],
	);

	const onSendClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		openShareClipModal(clip.id, "Send");
	}, [clip]);

	const onShareClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		openShareClipModal(clip.id, "Share");
	}, [clip]);

	const [duration, setDuration] = React.useState<number | null>(null);

	const onVideoLoadedMetadata = React.useCallback(() => {
		if (!videoRef.current) return;
		if (isNaN(videoRef.current.duration)) return;

		setDuration(videoRef.current.duration);
	}, [setDuration, videoRef]);

	const [videoPlayerContainer] = React.useState(() => {
		const videoPlayerDiv = document.createElement("div");
		videoPlayerDiv.style.width = "100%";
		videoPlayerDiv.style.height = "100%";
		return videoPlayerDiv;
	});

	function handleVideoContainerRef(el: HTMLElement | null): void {
		if (!el || el?.contains(videoPlayerContainer)) {
			return;
		}
		el?.appendChild(videoPlayerContainer);
	}

	const getClipURL = useGetURL(clip);
	const onCopyClick = React.useCallback(() => {
		if (!getClipURL) return;
		copyToClipboard(getClipURL());
		dispatchAnalyticsTrack("[AI Copilot] Copied Link to Clip", { fingerprint: analyticsFingerprint });
	}, [analyticsFingerprint, getClipURL]);

	const modals = useModals();
	const onRenameClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		modals.open(({ close }) => <RenameClipModal onClose={close} clip={clip} />);
	}, [clip, modals]);

	// Elements rendering

	const transcriptEl = (
		<Transcript
			ref={transcriptRef}
			note={note}
			viewer={null}
			meetingRecording={clip}
			transcriptDeletionReason={null}
			transcript={clip.transcript}
			onSegmentClick={onTranscriptSegmentClick}
			videoDuration={duration}
			closeModal={noop}
			isClip={true}
		/>
	);

	const summaryEl = (
		<div className={transcriptModalStyles.publicClipSummaryContainer}>
			<h2 className={transcriptModalStyles.publicClipSummaryHeading}>
				Summary
				<small className={transcriptModalStyles.publicClipSummaryDuration}>
					{formatDuration(clip.clipEnd - clip.clipStart)}
				</small>
			</h2>
			<SummaryText className={transcriptModalStyles.publicClipSummaryText}>{clip.summary ?? ""}</SummaryText>
		</div>
	);

	const statsEl = (
		<div className={styles.statsContainer}>
			<React.Suspense fallback={<LoadingSpinner inline dimmer={false} delay={150} />}>
				<RecapEngagementSection meetingRecordingOrClipId={clip.id} />
			</React.Suspense>
		</div>
	);

	let selectedTabElement = summaryEl;
	if (activeTab === "Transcript") {
		selectedTabElement = transcriptEl;
	} else if (activeTab === "Stats") {
		selectedTabElement = statsEl;
	}

	const [videoCollapsed, setVideoCollapsed] = React.useState(false);
	const toggleVideoCollapsed = React.useCallback(() => setVideoCollapsed(!videoCollapsed), [videoCollapsed]);

	const videoEl = (
		<TranscriptVideo
			key={clip.id}
			ref={videoRef}
			onTimeUpdate={onVideoTimeUpdate}
			onLoadedMetadata={onVideoLoadedMetadata}
			meetingRecording={clip}
			initialVideoTimestamp={initialVideoTimestamp}
			videoPlaybackSettings={viewer.videoPlaybackSettings}
			isMiniPlayerActive={isMiniPlayerActive}
			toggleMiniPlayer={toggleMiniPlayer}
			thumbnailTrack={null}
			allowRecapDownload={viewer?.account.allowRecapDownload}
			isClip
			isCollapsed={videoCollapsed}
			setIsCollapsed={setVideoCollapsed}
			inClippingMode={false}
		/>
	);

	const sharedWithChannelsCount = clip.postedInChannels.length;
	const sharedWithChannelsTooltip =
		`Shared with ${sharedWithChannelsCount}` + ` channel${sharedWithChannelsCount === 1 ? "" : "s"}`;

	const recording = clip.baseRecording;
	const start = recording?.note?.eventStart ?? recording?.meetingCallStart;
	const end = recording?.note?.eventEnd ?? recording?.meetingCallEnd;

	const noteDateEvent = start && end ? { start: new Date(start), end: new Date(end), isAllDay: false } : null;

	const openModal = React.useCallback(() => {
		openRecapModal({ noteId: note?.id, recordingId: recording?.id });
		close();
	}, [close, note?.id, recording?.id]);

	const clipInfo = recording ? (
		<p className={cx(styles.clipInfoContainer, { [styles.narrowViewport]: narrowViewport })}>
			Clipped from:{" "}
			<b className={styles.noteTitle}>
				{note ? <ButtonLink onClick={openModal}>{recording.title}</ButtonLink> : recording.title}
			</b>
			<span className={styles.noteDateSeparator} />
			<NoteDate createdAt={null} isCancelled={false} event={noteDateEvent} />
		</p>
	) : (
		<p className={styles.clipInfoContainer}>Clipped from a private recording.</p>
	);

	const content = isMiniPlayerActive ? (
		<MiniPlayer
			title={clip.title || ""}
			onClose={close}
			toggleMiniPlayer={toggleMiniPlayer}
			collapsed={clip.mediaType === "AUDIO"}
		>
			<div ref={handleVideoContainerRef} className={transcriptModalStyles.miniPlayer} />
		</MiniPlayer>
	) : (
		<Modal
			onClose={narrowViewport ? close : onDimmerClickEnterMiniPlayer}
			width={narrowViewport ? "100%" : "67rem"}
			height={narrowViewport ? "100%" : "53rem"}
			overlayClassName={transcriptModalStyles.overlayReducedPadding}
			className={styles.modal}
		>
			<Modal.Header noBottomBorder className={styles.modalHeader}>
				<Modal.Title className={styles.modalTitle} tabIndex={0}>
					{clip.title}
				</Modal.Title>
				<Modal.HeaderActions
					onClose={close}
					className={cx(transcriptModalStyles.modalHeaderActions, styles.modalHeaderActions)}
				>
					{clip.viewerCanEdit && (
						<>
							<div className={styles.shareContainer}>
								<Tooltip placement="top" content="Share to channel">
									<IconButton icon={faPlus} className={styles.plusButton} onClick={onShareClick} />
								</Tooltip>
								{sharedWithChannelsCount > 0 && (
									<Tooltip placement="top" content={sharedWithChannelsTooltip}>
										<Button
											size="normal"
											variant="secondary"
											className={styles.channelsCountButton}
											onClick={onShareClick}
										>
											{sharedWithChannelsCount > 9 ? "9+" : sharedWithChannelsCount}
										</Button>
									</Tooltip>
								)}
							</div>
							<Button size="slim" variant="tertiary" className={styles.sendButton} onClick={onSendClick}>
								<FontAwesomeIcon icon={faPaperPlane} />
								Send
							</Button>
						</>
					)}
					<Menu key="more-actions-menu" mobileHeaderTitle="More actions">
						<Menu.Trigger>
							<IconButton icon={faEllipsisV} />
						</Menu.Trigger>
						<Menu.Content align="end">
							{clip.viewerCanEdit && (
								<Menu.Item icon={faEdit} onSelect={onRenameClick}>
									Rename
								</Menu.Item>
							)}
							<Menu.Item icon={faLink} onSelect={onCopyClick}>
								Copy link
							</Menu.Item>
						</Menu.Content>
					</Menu>
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body noPadding className={transcriptModalStyles.recapModalBody}>
				<div key="preview" className={transcriptModalStyles.preview}>
					{!narrowViewport && (
						<div key="left-panel" className={cx(transcriptModalStyles.leftPanel, styles.leftPanel)}>
							{clipInfo}

							{/* Only show the toggle group if there's more than one tab */}
							{TAB_VALUES.length > 1 && (
								<div className={styles.toggleGroupContainer}>
									<ToggleGroup
										key="toggle-group"
										className={cx(transcriptModalStyles.leftPanelToggleGroup, styles.toggleGroup)}
										itemClassName={transcriptModalStyles.leftPanelToggleGroupItem}
										options={TAB_VALUES.map(value => ({ value, label: value }))}
										value={activeTab}
										onValueChange={setActiveTab}
									/>
								</div>
							)}

							{selectedTabElement}
						</div>
					)}
					<div key="metadata" className={transcriptModalStyles.metadata}>
						<div key="container-el" ref={handleVideoContainerRef} />
						<CollapseButton isCollapsed={videoCollapsed} onClick={toggleVideoCollapsed} mediaType={clip.mediaType} />
						{narrowViewport ? (
							<>
								{clipInfo}
								<ToggleGroup
									key="toggle-group"
									className={transcriptModalStyles.narrowWidthToggleGroup}
									options={MOBILE_TAB_VALUES.map(value => ({ value, label: value }))}
									value={activeTab}
									onValueChange={setActiveTab}
								/>
								{selectedTabElement}
							</>
						) : (
							summaryEl
						)}
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);

	useSessionManager(videoRef.current, null, clip.id);

	return (
		<>
			{ReactDOM.createPortal(videoEl, videoPlayerContainer)}
			{content}
		</>
	);
}

const clipQuery = graphql`
	query ClipModalQuery($id: ID!) {
		node(id: $id) {
			__typename
			...ClipModal_clip
		}

		viewer {
			...TranscriptModal_viewer @arguments(isPublic: false)
		}
	}
`;

type ClipModalRendererProps = ModalProps<unknown> & {
	clipId: string;
	initialVideoTimestamp?: number;
	clipTab?: ClipTabType;
};

function ClipModalRenderer({ clipId, initialVideoTimestamp, close, clipTab }: ClipModalRendererProps) {
	const { node: clip, viewer: viewerKey } = useLazyLoadQuery<ClipModalQuery>(
		clipQuery,
		{ id: clipId },
		{ fetchPolicy: "network-only" },
	);

	if (clip?.__typename !== "MeetingRecordingClip") {
		return null;
	}

	return (
		<TranscriptModalContextProvider>
			<ClipModal
				close={close}
				clipKey={clip}
				viewerKey={viewerKey}
				initialVideoTimestamp={initialVideoTimestamp}
				clipTab={clipTab}
			/>
		</TranscriptModalContextProvider>
	);
}

const ClipModalRendererWithSuspense = withSuspense(<LoadingFallback />, ClipModalRenderer);

export function isClipTab(tab?: string): tab is ClipTabType {
	if (!tab) return false;
	return ["Transcript", "Stats", "Summary"].includes(tab);
}

export function openClipModal(clipId: string, initialVideoTimestamp?: number, clipTab?: ClipTabType) {
	__globalModalContext?.open(
		({ close }) => (
			<ClipModalRendererWithSuspense
				close={close}
				clipId={clipId}
				initialVideoTimestamp={initialVideoTimestamp}
				clipTab={clipTab}
			/>
		),
		{ exclusiveId: CLIP_MODAL_ID, persistOnNavigation: true },
	);
}

export function closeClipModal() {
	clipModalEmitter.emit(CLIP_MODAL_CLOSE_EVENT);
}

export default ClipModalRendererWithSuspense;
